import { GridCellParams, GridColDef, GridColumnHeaderParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import PurchaseOrderCell from './Cells/PurchaseOrderCell/PurchaseOrderCell';
import ActionsCell from './Cells/ActionsCell/ActionsCell';
import StartDateCell from './Cells/StartDateCell/StartDateCell';
import EndDateCell from './Cells/EndDateCell/EndDateCell';
import PriceCell from './Cells/PriceCell/PriceCell';
import SpecRefCell from './Cells/SpecRefCell/SpecRefCell';
import RefChronoCell from './Cells/RefChronoCell/RefChronoCell';
import DefaultCell from './Cells/DefaultCell/DefaultCell';

import { find, forEach, map } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PurchaseOrders from 'types/entities/PurchaseOrders';
import { useSelector } from 'store/hooks';

type PurchaseOrderCellParams<T> = GridCellParams<T, PurchaseOrders, any>;
// type HelpMenuGuideGridCellParams<T> = GridCellParams<T, HelpMenuUserGuide, any>;

export const columnField = {
  PURCHASEORDER: 'poref',
  STARTDATE: 'postart',
  ENDDATE: 'poend',
  PRICE: 'poservice',
  SPECREFERENCE: 'specref_ws',
  REFERENCECHRONO: 'referencechrono_offreref',
  ACTIONS: 'actions',
  DEFAULT: 'file',
} as const;

const defaultColumnParams: GridColDef = {
  field: '',
  headerName: '',
  align: 'left',
  headerAlign: 'center',
  filterable: false,
  sortable: true,
  flex: 1,
};

export const columnsParams: GridColDef[] = [
  {
    field: columnField.PURCHASEORDER,
    headerName: 'Purchase Order',
    renderCell: (params: PurchaseOrderCellParams<number>) => <PurchaseOrderCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 60,
  },
  {
    field: columnField.STARTDATE,
    headerName: 'Start Date',
    renderCell: (params: PurchaseOrderCellParams<number>) => <StartDateCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 60,
  },
  {
    field: columnField.ENDDATE,
    headerName: 'End Date',
    renderCell: (params: PurchaseOrderCellParams<number>) => <EndDateCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 60,
  },
  {
    field: columnField.PRICE,
    headerName: 'Catalogues',
    renderCell: (params: PurchaseOrderCellParams<number>) => <PriceCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 60,
  },
  {
    field: columnField.SPECREFERENCE,
    headerName: 'Spec.Reference',
    renderCell: (params: PurchaseOrderCellParams<number>) => <SpecRefCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 60,
  },
  {
    field: columnField.REFERENCECHRONO,
    headerName: 'Reference (Chrono)',
    renderCell: (params: PurchaseOrderCellParams<number>) => <RefChronoCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 60,
  },
  {
    field: columnField.ACTIONS,
    headerName: 'Actions',
    renderCell: (params: PurchaseOrderCellParams<null>) => <ActionsCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 50,
    sortable: false,
  },
  {
    field: columnField.DEFAULT,
    headerName: 'File',
    renderCell: (params: PurchaseOrderCellParams<number>) => <DefaultCell {...params.row} />,
    resizable: true,
    align: 'center',
    minWidth: 60,
    sortable: false,
  },
];

type ColumnsFields = { [name: string]: string[] };

export const PurchaseOrderFields: ColumnsFields = {
  purchaseOrder: [
    columnField.PURCHASEORDER,
    columnField.STARTDATE,
    columnField.ENDDATE,
    columnField.PRICE,
    columnField.SPECREFERENCE,
    columnField.REFERENCECHRONO,
    columnField.ACTIONS,
    columnField.DEFAULT,
  ],
};

/**
 * React hook to build columns variable for Mui Datagrid with specific components
 */
export const useColumns = (columnsFields: ColumnsFields, order_type_id: number) => {
  const [t] = useTranslation();
  const state = useSelector((state) => state.purchaseOrder);
  return useMemo(() => {
    const columns: { [name: string]: GridColDef<PurchaseOrders, any, any>[] } = {};
    forEach(columnsFields, (fields, name) => {
      columns[name] = map(fields, (field) => {
        const columnParam = find(columnsParams, ['field', field]);
        return {
          ...defaultColumnParams,
          ...columnParam,
          headerName: columnParam?.headerName ? t(columnParam.headerName) : t(field),
        };
      });
    });

    return columns;
  }, [columnsFields, t, state]);
};
